import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/UcusBilgileri.css';
import { Container } from 'react-bootstrap';
import Header from '../components/Header';
import TabBar from '../components/TabBar';

interface UcusBilgisi {
  destination: string;
  flight: string;
  airline: string;
  status: string;
  time: string;
  date: string;
}

const UcusBilgileri: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gelenUcuslar, setGelenUcuslar] = useState<UcusBilgisi[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    // Uçuş verilerini getir
    fetchUcusBilgileri();

    // Belirli aralıklarla verileri güncelle
    const ucusInterval = setInterval(fetchUcusBilgileri, 900000); // 15 dakika

    // Temizleme fonksiyonu
    return () => {
      clearInterval(ucusInterval);
    };
  }, [navigate]);

  // Uçuş bilgilerini getir
  const fetchUcusBilgileri = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://crm.havalimanitaksi.com.tr/listajax.php?Sayfa=FlightList');
      if (response.data && response.data.arrivals) {
        setGelenUcuslar(response.data.arrivals);
      }
    } catch (error) {
      console.error('Uçuş verileri alınamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  // Uçuş filtreleme
  const filterUcuslar = (ucus: UcusBilgisi) => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    return (
      ucus.destination.toLowerCase().includes(searchLower) ||
      ucus.flight.toLowerCase().includes(searchLower) ||
      ucus.airline.toLowerCase().includes(searchLower) ||
      ucus.status.toLowerCase().includes(searchLower)
    );
  };

  // Ana sayfaya dön
  const handleBackClick = () => {
    navigate('/');
  };

  // Yenile butonu
  const handleRefresh = () => {
    fetchUcusBilgileri();
  };

  // Durum ikonunu belirle
  const getStatusInfo = (status: string) => {
    const statusLower = status.toLowerCase();
    
    if (statusLower.includes('indi') || statusLower.includes('kalkti')) {
      return { 
        icon: 'fas fa-check-circle', 
        class: 'text-success',
        text: 'Tamamlandı'
      };
    } else if (statusLower.includes('gecik')) {
      return { 
        icon: 'fas fa-exclamation-triangle', 
        class: 'text-warning',
        text: 'Gecikti'
      };
    } else if (statusLower.includes('iptal')) {
      return { 
        icon: 'fas fa-times-circle', 
        class: 'text-danger',
        text: 'İptal'
      };
    } else if (statusLower.includes('yolda')) {
      return { 
        icon: 'fas fa-plane', 
        class: 'text-info',
        text: 'Yolda'
      };
    } else {
      return { 
        icon: 'fas fa-clock', 
        class: 'text-primary',
        text: 'Planlandı'
      };
    }
  };

  const filteredUcuslar = gelenUcuslar.filter(filterUcuslar);

  return (
    <div className="ucusbilgileri-container" style={{ paddingTop: 70 }}>
      <Header title="Uçuş Bilgilerim" />
      <Container className="py-4">
        {/* Ana İçerik */}
        <main className="ucus-main">
          {/* Arama Çubuğu */}
          <div className="search-bar">
            <div className="search-icon">
              <i className="fas fa-search"></i>
            </div>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Uçuş, havayolu veya varış noktası ara..." 
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button className="clear-button" onClick={() => setSearchText('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {/* İstatistik Çubuğu */}
          <div className="stats-bar">
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-plane"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Toplam Uçuş</div>
                <div className="stat-value">{gelenUcuslar.length}</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-filter"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Filtrelenmiş</div>
                <div className="stat-value">{filteredUcuslar.length}</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <i className="fas fa-sync-alt"></i>
              </div>
              <div className="stat-details">
                <div className="stat-label">Son Güncelleme</div>
                <div className="stat-value">{new Date().toLocaleTimeString('tr-TR')}</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="global-loading">
              <div className="taxi-loader">
                <i className="fas fa-taxi"></i>
              </div>
              <div className="loading-text">Uçuş Bilgileri Yükleniyor...</div>
            </div>
          ) : (
            <div className="ucus-list">
              {filteredUcuslar.length > 0 ? (
                filteredUcuslar.map((ucus, index) => {
                  const statusInfo = getStatusInfo(ucus.status);
                  
                  return (
                    <div key={index} className="ucus-item">
                      <div className="ucus-status-indicator" data-status={statusInfo.text}>
                        <i className={`${statusInfo.icon} ${statusInfo.class}`}></i>
                      </div>
                      <div className="ucus-content">
                        <div className="ucus-primary-info">
                          <div className="ucus-time-container">
                            <div className="ucus-time">
                              <i className="far fa-clock"></i> {ucus.time}
                            </div>
                            <div className="ucus-date">{ucus.date}</div>
                          </div>
                          <div className="ucus-destination">
                            <i className="fas fa-plane-arrival"></i>
                            <span>{ucus.destination}</span>
                          </div>
                        </div>
                        <div className="ucus-secondary-info">
                          <div className="ucus-flight-number">
                            <i className="fas fa-tag"></i> {ucus.flight}
                          </div>
                          <div className="ucus-airline">
                            <i className="fas fa-building"></i> {ucus.airline}
                          </div>
                          <div className={`ucus-status ${statusInfo.class}`}>
                            <i className={statusInfo.icon}></i> {ucus.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-ucus">
                  <i className="fas fa-search"></i>
                  <p>Arama kriterlerinize uygun uçuş bulunamadı.</p>
                </div>
              )}
            </div>
          )}

          {/* Yenile Butonu */}
          <button className="refresh-button" onClick={handleRefresh}>
            <i className="fas fa-sync-alt"></i>
          </button>
        </main>
      </Container>
      
      {/* Tab Bar Bileşeni */}
      <TabBar />
    </div>
  );
};

export default UcusBilgileri; 